import { ViewType } from 'types'
import { isCondition } from '../../ViewPreferences/components/ViewModifierControls/Filter/components/FilterList/FilterList'
import { Join, Operator } from '../../ViewPreferences/components/ViewModifierControls/Filter/constants'
import { Property } from 'api/clients/PropertyClient'
import { TreeFilesView } from 'screens/Finder/FinderContextTypes'
import { Condition, Filters, Group } from '../../ViewPreferences/components/ViewModifierControls/Filter/Filter'
import { FilesView } from 'api/clients/FilesViewsClient'
import dayjs from 'dayjs'

export type DayGridType = 'dayGridMonth' | 'dayGridWeek' | 'timeGridWeek'

// Replace any filters already in place using property that will now be the calendar prop,
// with the expected NotEmpty filter used to display events inside the calendar
// First, filter out the group whose propertyId equals the newly selected calendar property (if any).
// Then, map the groups so that the current calendar property is replaced by the newly selected one.
export const replaceCalendarPropFilters = ({
  filesView,
  property,
}: {
  filesView: TreeFilesView
  property: Property
}) => {
  const filters = filesView.schema.filters
  if (!filters) return filters

  return filteredGroupsWithoutCalendarProp(filters, property.id).map((group) => ({
    ...group,
    conditions: group.conditions
      .map((condition) =>
        conditionIsForCalendarProperty(condition, filesView?.schema.calendar_property_id)
          ? calendarPropertyCondition(property.id)
          : condition,
      )
      .flat(),
  }))
}

// If current view is not a calendar, return groups as they are.
// Else, return groups without any existing filter that was already using the calendar property.
//
// Used for displaying the list of filters and/or calculating the amount of filters, so that if
// current view is calendar, the calendar property ins't shown nor count
export const filterGroupsWithoutCalendarProperty = ({
  filesView,
  groups,
}: {
  filesView?: TreeFilesView
  groups?: Group[]
}) => {
  let groupsToFilter = groups || filesView?.schema.filters?.groups
  return filesView?.schema.default_view !== ViewType.Calendar
    ? groupsToFilter || []
    : groupsToFilter?.filter(
        (group) =>
          !group.conditions.find((condition) =>
            conditionIsForCalendarProperty(condition, filesView?.schema.calendar_property_id),
          ),
      ) || []
}

export const isCalendarProperty = ({
  filesView,
  propertyId,
}: {
  filesView?: TreeFilesView
  propertyId?: number
}): boolean =>
  filesView?.schema.default_view === ViewType.Calendar && propertyId === filesView?.schema.calendar_property_id

// Returns an array of filters with the calendar property filter at the beginning, and the rest of
// of the filters afterwards, removing any existing filters that were already using the calendar property.
// It needs to be put at the beginning so that the indexing of the filters list works correctly (in FilersList component)
export const filtersForCalendarView = ({
  filesView,
  newCalendarPropertyId,
  forUnscheduled = false,
  dateRange,
}: {
  filesView?: FilesView
  newCalendarPropertyId?: number | null
  forUnscheduled?: boolean
  dateRange?: { start: Date; end: Date }
}): Filters | undefined => {
  if (!filesView) {
    if (!newCalendarPropertyId) return undefined

    return {
      glue: Join.And,
      groups: [calendarPropertyGroup(newCalendarPropertyId, forUnscheduled, dateRange)],
    }
  }

  const filters = filesView.schema.filters
  const calendarPropertyId = filesView.schema.calendar_property_id || newCalendarPropertyId

  if (!calendarPropertyId) {
    if (!filters?.groups)
      return {
        glue: Join.And,
        groups: [],
      }

    return filters
  }

  if (!filters || !filters.groups || !filters.groups.length)
    return {
      glue: Join.And,
      groups: [calendarPropertyGroup(calendarPropertyId, forUnscheduled, dateRange)],
    }

  return {
    ...filters,
    groups: [calendarPropertyGroup(calendarPropertyId, forUnscheduled, dateRange)].concat(
      filteredGroupsWithoutCalendarProp(filters, calendarPropertyId),
    ),
  }
}

// If neither the old not new view types are of calendar type, or the new calendar property is different from the last one, return filters as they are
// Else, if the new view type is not calendar, remove the calendar prop filter fro the new filters.
// Else, add the calendar prop filter to the list of filters
export const newFiltersForView = (filesView: FilesView, viewType: ViewType, calendar_property_id: number | null) => {
  if (
    (viewType !== ViewType.Calendar && filesView.schema.default_view !== ViewType.Calendar) ||
    calendar_property_id !== filesView.schema.calendar_property_id
  )
    return filesView.schema.filters

  return viewType !== ViewType.Calendar && filesView.schema.filters
    ? {
        ...filesView.schema.filters,
        groups: filteredGroupsWithoutCalendarProp(filesView.schema.filters, calendar_property_id),
      }
    : filtersForCalendarView({
        filesView,
        newCalendarPropertyId: calendar_property_id,
      })
}

export const filteredGroupsWithoutCalendarProp = (filters: Filters, calendarPropertyId: number | null) =>
  filters.groups?.filter(
    (group) => !group.conditions.find((condition) => conditionIsForCalendarProperty(condition, calendarPropertyId)),
  )

// When stringifying the filters, we need to pass this as the replacer so that the outcome is always sorted the same
export const filtersSorter = (_key: string, value: Group[] | Condition[]) =>
  value instanceof Object && !(value instanceof Array)
    ? Object.keys(value)
        .sort()
        .reduce((sorted: any, key: string) => {
          sorted[key] = value[key]
          return sorted
        }, {})
    : value

export const calendarPropertyCondition = (calendarPropertyId: number): Condition =>
  ({
    operand: { kind: 'property', property_id: calendarPropertyId },
    operator: Operator.NotEmpty,
    value: [],
  } as Condition)

export const dateRangeCondition = (calendarPropertyId: number, start: Date, end: Date): Condition => {
  const startDate = dayjs(start).toDate()
  const endDate = dayjs(end).toDate()

  return {
    operand: { kind: 'property', property_id: calendarPropertyId },
    operator: Operator.Between,
    value: [startDate, endDate],
  } as Condition
}

export const unscheduledPropertyCondition = (calendarPropertyId: number): Condition =>
  ({
    operand: { kind: 'property', property_id: calendarPropertyId },
    operator: Operator.Empty,
    value: [],
  } as Condition)

export const calendarConditions = (calendarPropertyId: number, dateRange?: { start: Date; end: Date }): Condition[] => {
  const conditions = [calendarPropertyCondition(calendarPropertyId)]
  if (dateRange) conditions.push(dateRangeCondition(calendarPropertyId, dateRange.start, dateRange.end))
  return conditions
}

export const calendarPropertyGroup = (
  calendarPropertyId: number,
  forUnscheduled: boolean = false,
  dateRange: { start: Date; end: Date } | undefined = undefined,
): Group =>
  ({
    glue: Join.And,
    conditions: forUnscheduled
      ? [unscheduledPropertyCondition(calendarPropertyId)]
      : calendarConditions(calendarPropertyId, dateRange),
  } as Group)

const conditionIsForCalendarProperty = (condition: Condition | Group, propertyId: number | null): boolean =>
  isCondition(condition) && condition.operand.property_id === propertyId
